import { useState, FormEvent } from 'react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formUrl, setFormUrl] = useState<string>('');

  // Get form URL on component mount
  useState(() => {
    fetch('https://readdy.ai/api/public/form/get-url', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: 'Консультация по проекту' })
    })
      .then(res => res.json())
      .then(data => setFormUrl(data.submitAddr))
      .catch(err => console.error('Error getting form URL:', err));
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!formUrl) return;

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);

      const response = await fetch(formUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', phone: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="bg-gradient-to-br from-[#2B2D42] to-[#1A1D35] py-24">
      <div className="max-w-5xl mx-auto px-8">
        {/* Header */}
        <div className="text-center mb-14">
          <h2 className="text-6xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Начнем ваш проект?
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed max-w-2xl mx-auto" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Оставьте заявку, и наш эксперт свяжется с вами в течение 24 часов для обсуждения деталей вашего проекта
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} data-readdy-form id="contact-form" className="mb-20">
          <div className="flex gap-4 mb-6">
            <input
              type="text"
              name="name"
              placeholder="Ваше имя"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="flex-1 bg-white rounded-xl px-6 py-4 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#D4745E]"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="flex-1 bg-white rounded-xl px-6 py-4 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#D4745E]"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            />
            <input
              type="tel"
              name="phone"
              placeholder="Телефон"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              required
              className="flex-1 bg-white rounded-xl px-6 py-4 text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-[#D4745E]"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="bg-[#D4745E] text-white px-10 py-4 rounded-xl font-semibold hover:bg-[#C86550] transition-all flex items-center gap-3 whitespace-nowrap cursor-pointer disabled:opacity-50"
              style={{ fontFamily: 'Montserrat, sans-serif' }}
            >
              {isSubmitting ? 'Отправка...' : 'Получить консультацию'}
              <i className="ri-arrow-right-line text-xl"></i>
            </button>
          </div>
          
          {submitStatus === 'success' && (
            <p className="text-green-400 text-center text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Спасибо! Ваша заявка успешно отправлена.
            </p>
          )}
          {submitStatus === 'error' && (
            <p className="text-red-400 text-center text-sm" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Произошла ошибка. Пожалуйста, попробуйте еще раз.
            </p>
          )}
        </form>

        {/* Contact Info */}
        <div className="grid grid-cols-3 gap-8">
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
              <i className="ri-phone-line text-2xl text-white"></i>
            </div>
            <p className="text-sm text-gray-400 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Телефон</p>
            <p className="text-base font-semibold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>+90 555 123 4567</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
              <i className="ri-mail-line text-2xl text-white"></i>
            </div>
            <p className="text-sm text-gray-400 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Email</p>
            <p className="text-base font-semibold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>info@unitycore.com</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-4">
              <i className="ri-map-pin-line text-2xl text-white"></i>
            </div>
            <p className="text-sm text-gray-400 mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>Адрес</p>
            <p className="text-base font-semibold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>Стамбул, Турция</p>
          </div>
        </div>
      </div>
    </section>
  );
}
